import CancelablePromise from "cancelable-promise";
import {
  AtlasViewBoxRequest,
  BaseDocument,
  BlobContentType,
  CoreApi,
  CountResponse,
  FetchContentType,
  MapViewBoxRequest,
  OptionsType,
  Properties,
  RequestOptions,
  ViewBoxRequest,
  BaseSearchPinRequest,
} from "@hokuto/jam-core";
import { ApiMethod, FetchOptions } from "../types.js";
import {
  blobFetch,
  jsonFetch,
  pngFetch,
  svgFetch,
  uploadFetch,
} from "../utils/api-fetch.js";
import { getAtlasImageParams, getMapImageParams } from "./params.js";

export function fetchJSON<
  Req extends OptionsType & { values?: Properties },
  Res = unknown
>(coreApi: (args: Req) => string, method = ApiMethod.Get) {
  return function fetchJSON(args: Req): CancelablePromise<Res> {
    const url = coreApi(args);
    const headers = CoreApi.Options.getRequestOptions(args);
    const { values: params } = args;
    return jsonFetch(url, { headers, method, params });
  };
}

export function fetchBlob<Req extends OptionsType & { values?: Properties }>(
  coreApi: (args: Req) => string,
  contentType: BlobContentType
) {
  return function fetchBlob(args: Req): CancelablePromise<Blob> {
    const url = coreApi(args);
    const headers = CoreApi.Options.getRequestOptions(args);
    const { values: params } = args;
    return blobFetch(url, { headers, params }, contentType);
  };
}

export function fetchUpload<
  Req extends OptionsType & { body: FormData },
  Res = unknown
>(coreApi: (args: Req) => string, contentType?: FetchContentType) {
  return function fetchBlob(args: Req): CancelablePromise<Res> {
    const url = coreApi(args);
    const headers = CoreApi.Options.getRequestOptions(args);
    const { body } = args;
    return uploadFetch(
      url,
      { headers, method: ApiMethod.Post, body },
      contentType
    );
  };
}

export function fetchMapImage(coreApi: typeof CoreApi.Map.Image) {
  return function fetchMapImage(
    args: MapViewBoxRequest,
    body?: Partial<RequestOptions>,
    priority?: FetchOptions["priority"]
  ): CancelablePromise<Blob> {
    const { url, headers, method, params } = getMapImageParams(
      coreApi,
      args,
      body
    );
    return pngFetch(url, { headers, method, params, priority });
  };
}

export function fetchAtlasImage(coreApi: typeof CoreApi.Atlas.Image) {
  return function fetchMapImage(
    args: AtlasViewBoxRequest,
    body?: Partial<RequestOptions>,
    priority?: FetchOptions["priority"]
  ): CancelablePromise<Blob> {
    const { url, headers, method, params } = getAtlasImageParams(
      coreApi,
      args,
      body
    );

    return pngFetch(url, { headers, method, params, priority });
  };
}

export function fetchMapSvg(coreApi: typeof CoreApi.Map.Svg) {
  return function fetchMapImage(
    args: MapViewBoxRequest,
    body?: Partial<RequestOptions>
  ): CancelablePromise<Blob> {
    const { url, headers, method, params } = getMapImageParams(
      coreApi,
      args,
      body
    );

    return svgFetch(url, { headers, method, params });
  };
}

export function fetchAtlasSvg(coreApi: typeof CoreApi.Atlas.Svg) {
  return function fetchMapImage(
    args: AtlasViewBoxRequest,
    body?: Partial<RequestOptions>
  ): CancelablePromise<Blob> {
    const { url, headers, method, params } = getAtlasImageParams(
      coreApi,
      args,
      body
    );

    return svgFetch(url, { headers, method, params });
  };
}

export class SearchBaseApi<Response extends Partial<BaseDocument>> {
  Items;
  Count;
  constructor(coreApi: InstanceType<typeof CoreApi.Url.SearchBaseUrl>) {
    this.Items = fetchJSON<BaseSearchPinRequest, Response[]>(coreApi.Items);
    this.Count = fetchJSON<BaseSearchPinRequest, CountResponse>(coreApi.Count);
  }
}

export class SearchFeatureApi<
  FeatureRequest extends BaseSearchPinRequest,
  BaseResponse extends Partial<BaseDocument>,
  FeatureResponse extends Partial<BaseDocument> = BaseResponse
> extends SearchBaseApi<BaseResponse> {
  Feature;
  constructor(coreApi: InstanceType<typeof CoreApi.Url.SearchFeatureUrl>) {
    super(coreApi);
    this.Feature = {
      Items: fetchJSON<FeatureRequest, FeatureResponse[]>(
        coreApi.Feature.Items
      ),
      Count: fetchJSON<FeatureRequest, CountResponse>(coreApi.Feature.Count),
    };
  }
}

export class SearchViewBoxApi<
  FeatureRequest extends BaseSearchPinRequest,
  BaseResponse extends Partial<BaseDocument>,
  FeatureResponse extends Partial<BaseDocument> = BaseResponse
> extends SearchFeatureApi<FeatureRequest, BaseResponse, FeatureResponse> {
  ViewBox;
  constructor(coreApi: InstanceType<typeof CoreApi.Url.SearchViewBoxUrl>) {
    super(coreApi);
    this.ViewBox = {
      Items: fetchJSON<ViewBoxRequest & FeatureRequest, FeatureResponse[]>(
        coreApi.ViewBox.Items
      ),
      Count: fetchJSON<ViewBoxRequest & FeatureRequest, CountResponse>(
        coreApi.ViewBox.Count
      ),
    };
  }
}
